import React, { useState, useCallback, useContext, createContext } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

/**
 * A reusable modal component that replaces browser prompt() and confirm() dialogs
 * with translatable React modals.
 *
 * Usage with Context (works in class and functional components):
 *   // Wrap your app with PromptModalProvider
 *   <PromptModalProvider>
 *     <App />
 *   </PromptModalProvider>
 *
 *   // In functional components:
 *   const { showPrompt, showConfirm } = usePromptModalContext();
 *
 *   // In class components:
 *   static contextType = PromptModalContext;
 *   this.context.showPrompt({ title: '...', message: '...' });
 */

// Create context for global modal access
export const PromptModalContext = createContext(null);

export function usePromptModalContext() {
  const context = useContext(PromptModalContext);
  if (!context) {
    throw new Error('usePromptModalContext must be used within PromptModalProvider');
  }
  return context;
}

// Provider component that wraps your app
export function PromptModalProvider({ children }) {
  const { t } = useTranslation();
  const [modalState, setModalState] = useState({
    show: false,
    title: '',
    message: '',
    defaultValue: '',
    isPrompt: true,
    resolve: null,
  });
  const [inputValue, setInputValue] = useState('');

  const showPrompt = useCallback(({ title, message, defaultValue = '' }) => {
    return new Promise((resolve) => {
      setInputValue(defaultValue);
      setModalState({
        show: true,
        title,
        message,
        defaultValue,
        isPrompt: true,
        resolve,
      });
    });
  }, []);

  const showConfirm = useCallback(({ title, message }) => {
    return new Promise((resolve) => {
      setModalState({
        show: true,
        title,
        message,
        defaultValue: '',
        isPrompt: false,
        resolve,
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    const { resolve, isPrompt } = modalState;
    setModalState(prev => ({ ...prev, show: false }));
    if (resolve) {
      resolve(isPrompt ? inputValue : true);
    }
  }, [modalState, inputValue]);

  const handleCancel = useCallback(() => {
    const { resolve, isPrompt } = modalState;
    setModalState(prev => ({ ...prev, show: false }));
    if (resolve) {
      resolve(isPrompt ? null : false);
    }
  }, [modalState]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
  }, [handleConfirm]);

  return (
    <PromptModalContext.Provider value={{ showPrompt, showConfirm }}>
      {children}
      <Modal show={modalState.show} onHide={handleCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>{modalState.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalState.message && <p style={{ whiteSpace: 'pre-wrap' }}>{modalState.message}</p>}
          {modalState.isPrompt && (
            <Form.Control
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancel}>
            {t('common:buttons.cancel')}
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            {t('common:buttons.confirm')}
          </Button>
        </Modal.Footer>
      </Modal>
    </PromptModalContext.Provider>
  );
}

export default PromptModalProvider;
